import { useTranslations } from '@vocab/react';
import { Box, Stack, Text } from 'braid-design-system';
import React, { type ReactNode } from 'react';

import { useConfig } from 'src/app/shared/hooks/useConfig';
import VocabHtmlTags from 'src/app/utils/translation/vocabHtmlTags';
import { SalaryBar } from 'src/app/v1/views/components/Pay/components/SalaryBar';
import { PayType, type SuggestedSalary } from 'src/types/salaryTypes';

import translations from './.vocab';
import { formatLabel } from './utils/format-label';

interface PayRangeRecommendationProps {
  suggestedSalary: SuggestedSalary;
  minSalaryValue?: number;
  maxSalaryValue?: number;
  minSalaryRecommended: string;
  maxSalaryRecommended: string;
  payType: string;
}
export const PayRangeRecommendation = ({
  suggestedSalary,
  maxSalaryValue,
  minSalaryValue,
  maxSalaryRecommended,
  minSalaryRecommended,
  payType,
}: PayRangeRecommendationProps) => {
  const { t } = useTranslations(translations);

  const { roleTitle, location } = suggestedSalary;
  const frequencyLabels: Record<string, string> = {
    [PayType.ANNUAL_PACKAGE]: t('annually'),
    [PayType.ANNUAL_COMMISSION]: t('annually'),
    [PayType.COMMISSION_ONLY]: t('annually'),
    [PayType.HOURLY_RATE]: t('hourly'),
    [PayType.MONTHLY]: t('monthly'),
  };
  const frequencyLabel = frequencyLabels[payType];

  const locale = useConfig().getLocale();
  const { min, max, averageMin, averageMax, currency } = suggestedSalary;

  return (
    <Box padding="large" background="neutralLight">
      <Stack space="medium">
        <Text>
          {t('pay-range-recommendation-text', {
            roleTitle,
            location,
            minSalary: minSalaryRecommended,
            maxSalary: maxSalaryRecommended,
            frequencyLabel,
            Strong: (children: ReactNode[]) => VocabHtmlTags.strong(children),
          })}
        </Text>
        <SalaryBar
          scaleLabelOne={formatLabel(min, { locale, currency })}
          scaleLabelTwo={formatLabel(averageMin, { locale, currency })}
          scaleLabelThree={formatLabel(averageMax, { locale, currency })}
          scaleLabelFour={formatLabel(max, { locale, currency })}
          scaleValueMax={max}
          scaleValueMin={min}
          selectedValueMax={maxSalaryValue || 0}
          selectedValueMin={minSalaryValue || 0}
        />
      </Stack>
    </Box>
  );
};
